import { Alert, AlertIcon, Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useCallback, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { urls } from "../../../../../urls";
import { LastChangedTag } from "../../../../../common/editing/LastChangedTag/LastChangedTag";
import { useEntityState } from "../../../../../common/editing/save-coordinator";
import {
  useGetExclusionGroundsTaxonomyQuery,
  usePublishDpsCompetitionMutation,
  useSubmitDoffinNoticeMutation,
  type OrgDto,
  type PersonDto,
  type SourcingEventV2Dto,
} from "../../../../../autogen/bff-api";
import { isDpsCompetition } from "../utils/procedure";
import { useApiError } from "../../../../../common/errors/useApiError";
import { StepNavigation } from "../../../../../common/StepNavigation";
import type { PublicEventStep, WizardStep } from "../wizardSteps";
import { useReadinessByField } from "../hooks/useReadinessByField";
import { AwardCriteriaSection } from "../components/summary/AwardCriteriaSection";
import { ClassificationSection } from "../components/summary/ClassificationSection";
import { ContractingAuthoritySection } from "../components/summary/ContractingAuthoritySection";
import { DeadlinesAndMilestonesSection } from "../components/summary/DeadlinesAndMilestonesSection";
import { DocumentsSection } from "../components/summary/DocumentsSection";
import { MinimumRequirementsSection } from "../components/summary/MinimumRequirementsSection";
import { OverviewSection } from "../components/summary/OverviewSection";
import { ProcedureSection } from "../components/summary/ProcedureSection";
import { QualificationSection } from "../components/summary/QualificationSection";
import { computeExclusionGroundsSummary } from "../utils/computeExclusionGroundsSummary";
import { EspdPreviewSection } from "../components/espd/EspdPreviewSection";
import { DevFastForwardMenu } from "../../../../v2/sourcing-event/DevFastForwardMenu";

interface Props {
  eventId: string;
  organization: OrgDto;
  currentEditor: PersonDto;
  responsibleBuyer: PersonDto;
  responsibleEditControl?: ReactNode;
  previousStep?: () => void;
  navigateToStep: (step: PublicEventStep) => void;
  steps: ReadonlyArray<WizardStep>;
}

export const SummaryStep = ({
  eventId,
  organization,
  currentEditor,
  responsibleBuyer,
  responsibleEditControl,
  previousStep,
  navigateToStep,
  steps,
}: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const event = useEntityState<SourcingEventV2Dto>(eventId);
  const { readinessFor, isPublishReady, isLoading: readinessLoading, isError: readinessError } =
    useReadinessByField(eventId);
  const { data: taxonomy } = useGetExclusionGroundsTaxonomyQuery();
  const [submitDoffin, { isLoading: isSubmittingNotice, isSuccess: hasPublished }] = useSubmitDoffinNoticeMutation();
  const [publishDpsCompetition, { isLoading: isOpeningCompetition, isSuccess: hasOpenedCompetition }] =
    usePublishDpsCompetitionMutation();
  const isPublishing = isSubmittingNotice || isOpeningCompetition;
  const apiError = useApiError();

  const eventPublicContext = event?.eventContext.type === "Public" ? event.eventContext : null;
  const hasPublishedPersisted = useMemo(
    () =>
      (eventPublicContext?.publications ?? []).some(
        (p) => p.channel === "DOFFIN" && p.status !== "FAILED",
      ),
    [eventPublicContext?.publications],
  );
  const isPublished =
    hasPublished ||
    hasOpenedCompetition ||
    (isDpsCompetition(eventPublicContext?.procedureType)
      ? eventPublicContext?.dpsCompetition?.invitedAt != null
      : hasPublishedPersisted);

  // FOA § 26-7: opening the purchase freezes its invited set instead of going to Doffin.
  const isCompetitionUnderScheme = isDpsCompetition(eventPublicContext?.procedureType);

  const handlePublish = useCallback(async () => {
    try {
      if (isCompetitionUnderScheme) {
        await publishDpsCompetition({ eventId }).unwrap();
        toast({ title: t("The competition is open for tenders"), status: "success", duration: 6000 });
      } else {
        await submitDoffin({ sourcingEventId: eventId }).unwrap();
        toast({ title: t("Notice published to Doffin"), status: "success", duration: 6000 });
      }
      navigate(urls.v2.sourcingEvents.view.go(eventId), { replace: true });
    } catch (e) {
      apiError.trigger(e);
    }
  }, [isCompetitionUnderScheme, publishDpsCompetition, submitDoffin, eventId, toast, t, apiError, navigate]);

  const runsStep = (step: PublicEventStep) => steps.some((s) => s.key === step);

  const exclusionGroundsSummary = useMemo(
    () => computeExclusionGroundsSummary(taxonomy, eventPublicContext?.activeExclusionGroundIds ?? []),
    [taxonomy, eventPublicContext?.activeExclusionGroundIds],
  );

  if (!event) return null;

  const regulationNotYetSupported = readinessFor("RegulationSupportsPublishing")?.satisfied === false;

  return (
    <Box pb="8" pt="10">
      {readinessError && (
        <Alert status="error" borderRadius="lg" mb="6">
          <AlertIcon />
          <Text fontSize="sm">
            {t("Could not load publish readiness — refresh the page to retry. Section status indicators below may not be accurate.")}
          </Text>
        </Alert>
      )}
      {regulationNotYetSupported && (
        <Alert status="warning" borderRadius="lg" mb="6">
          <AlertIcon />
          <Text fontSize="sm">
            {t("Doffin announcements are not yet supported for this regulation. The procurement can be registered and made ready, but publishing stays unavailable until announcement support arrives.")}
          </Text>
        </Alert>
      )}
      <DeadlinesAndMilestonesSection
        event={event}
        readinessFor={readinessFor}
        onWizardStepNavigate={navigateToStep}
      />
      <OverviewSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      <ClassificationSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      <ProcedureSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      {runsStep("qualification") && (
        <QualificationSection
          event={event}
          readinessFor={readinessFor}
          onWizardStepNavigate={navigateToStep}
          exclusionGroundsSummary={exclusionGroundsSummary}
        />
      )}
      <AwardCriteriaSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      <MinimumRequirementsSection event={event} onWizardStepNavigate={navigateToStep} />
      <DocumentsSection event={event} fieldStatuses={[]} onWizardStepNavigate={navigateToStep} />
      <ContractingAuthoritySection
        organization={organization}
        currentEditor={currentEditor}
        responsibleBuyer={responsibleBuyer}
        responsibleEditControl={responsibleEditControl}
        buyerLegalType={eventPublicContext?.buyerLegalType}
        readinessFor={readinessFor}
      />
      {event.collectsEspdWithBid && (
        <EspdPreviewSection
          event={event}
          organization={organization}
          exclusionGroundsSummary={exclusionGroundsSummary}
        />
      )}

      <Flex justify="center" mb="6" mt="2" gap="4" align="center">
        <LastChangedTag timestamp={event.modifiedAt} />
        <DevFastForwardMenu
          event={event}
          onForcePublished={() => navigate(urls.v2.sourcingEvents.view.go(eventId), { replace: true })}
        />
      </Flex>

      <StepNavigation
        onPrevious={previousStep}
        onNext={handlePublish}
        nextLabel={isCompetitionUnderScheme ? t("Open for tenders") : t("Publish to Doffin")}
        nextIsAction
        isNextLoading={isPublishing}
        nextLoadingText={t("Publishing…")}
        isNextDisabled={readinessLoading || readinessError || isPublishing || isPublished || !isPublishReady}
        nextTooltip={
          readinessError
            ? t("Could not load readiness status — try refreshing the page")
            : isPublishing
            ? t("Publishing…")
            : isPublished
            ? isCompetitionUnderScheme
              ? t("The competition is already open")
              : t("Already published to Doffin")
            : regulationNotYetSupported
            ? t("Doffin announcements are not yet supported for this regulation")
            : !isPublishReady && !readinessLoading
            ? t("Fill in all required fields above to enable publishing")
            : null
        }
      />
    </Box>
  );
};

